
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { EtsyOrder } from '@/hooks/useEtsyOrders';
import { EtsyProduct } from '@/hooks/useEtsyProducts';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';

interface EtsyState {
  isConnected: boolean;
  isCheckingConnection: boolean;
  orders: EtsyOrder[];
  products: EtsyProduct[];
  isFetchingOrders: boolean;
  isFetchingProducts: boolean;
  lastOrdersFetch: Date | null;
  lastProductsFetch: Date | null;
  lastConnectionCheck: Date | null;
  // Pagination state
  ordersOffset: number;
  productsOffset: number;
  hasMoreOrders: boolean;
  hasMoreProducts: boolean;
  isLoadingMoreOrders: boolean;
  isLoadingMoreProducts: boolean;
  // Store selection - now supports multiple stores
  selectedStoreIds: string[];
}

type EtsyAction = 
  | { type: 'SET_CONNECTION_STATUS'; payload: boolean }
  | { type: 'SET_CHECKING_CONNECTION'; payload: boolean }
  | { type: 'SET_ORDERS'; payload: EtsyOrder[] }
  | { type: 'APPEND_ORDERS'; payload: EtsyOrder[] }
  | { type: 'SET_PRODUCTS'; payload: EtsyProduct[] }
  | { type: 'APPEND_PRODUCTS'; payload: EtsyProduct[] }
  | { type: 'SET_FETCHING_ORDERS'; payload: boolean }
  | { type: 'SET_FETCHING_PRODUCTS'; payload: boolean }
  | { type: 'SET_LOADING_MORE_ORDERS'; payload: boolean }
  | { type: 'SET_LOADING_MORE_PRODUCTS'; payload: boolean }
  | { type: 'SET_ORDERS_PAGINATION'; payload: { offset: number; hasMore: boolean } }
  | { type: 'SET_PRODUCTS_PAGINATION'; payload: { offset: number; hasMore: boolean } }
  | { type: 'SET_LAST_ORDERS_FETCH'; payload: Date | null }
  | { type: 'SET_LAST_PRODUCTS_FETCH'; payload: Date | null }
  | { type: 'SET_LAST_CONNECTION_CHECK'; payload: Date | null }
  | { type: 'SET_SELECTED_STORES'; payload: string[] }
  | { type: 'TOGGLE_STORE_SELECTION'; payload: string }
  | { type: 'CLEAR_DATA' }
  | { type: 'SET_STATE'; payload: Partial<EtsyState> };

const initialState: EtsyState = {
  isConnected: false,
  isCheckingConnection: false,
  orders: [],
  products: [],
  isFetchingOrders: false,
  isFetchingProducts: false,
  lastOrdersFetch: null,
  lastProductsFetch: null,
  lastConnectionCheck: null,
  ordersOffset: 0,
  productsOffset: 0,
  hasMoreOrders: false,
  hasMoreProducts: false,
  isLoadingMoreOrders: false,
  isLoadingMoreProducts: false,
  selectedStoreIds: [],
};

const etsyReducer = (state: EtsyState, action: EtsyAction): EtsyState => {
  switch (action.type) {
    case 'SET_CONNECTION_STATUS':
      return { ...state, isConnected: action.payload };
    case 'SET_CHECKING_CONNECTION':
      return { ...state, isCheckingConnection: action.payload };
    case 'SET_ORDERS':
      return { ...state, orders: action.payload, ordersOffset: 0 };
    case 'APPEND_ORDERS':
      return { ...state, orders: [...state.orders, ...action.payload] };
    case 'SET_PRODUCTS':
      return { ...state, products: action.payload, productsOffset: 0 };
    case 'APPEND_PRODUCTS':
      return { ...state, products: [...state.products, ...action.payload] };
    case 'SET_FETCHING_ORDERS':
      return { ...state, isFetchingOrders: action.payload };
    case 'SET_FETCHING_PRODUCTS':
      return { ...state, isFetchingProducts: action.payload };
    case 'SET_LOADING_MORE_ORDERS':
      return { ...state, isLoadingMoreOrders: action.payload };
    case 'SET_LOADING_MORE_PRODUCTS':
      return { ...state, isLoadingMoreProducts: action.payload };
    case 'SET_ORDERS_PAGINATION':
      return { 
        ...state, 
        ordersOffset: action.payload.offset, 
        hasMoreOrders: action.payload.hasMore 
      };
    case 'SET_PRODUCTS_PAGINATION':
      return { 
        ...state, 
        productsOffset: action.payload.offset, 
        hasMoreProducts: action.payload.hasMore 
      };
    case 'SET_LAST_ORDERS_FETCH':
      return { ...state, lastOrdersFetch: action.payload };
    case 'SET_LAST_PRODUCTS_FETCH':
      return { ...state, lastProductsFetch: action.payload };
    case 'SET_LAST_CONNECTION_CHECK':
      return { ...state, lastConnectionCheck: action.payload };
    case 'SET_SELECTED_STORES':
      localStorage.setItem('selectedEtsyStoreIds', JSON.stringify(action.payload));
      return { 
        ...state, 
        selectedStoreIds: action.payload,
        orders: [],
        products: [],
        ordersOffset: 0,
        productsOffset: 0,
        hasMoreOrders: false,
        hasMoreProducts: false,
        lastOrdersFetch: null,
        lastProductsFetch: null
      };
    case 'TOGGLE_STORE_SELECTION':
      const storeId = action.payload;
      const currentSelected = state.selectedStoreIds;
      const newSelected = currentSelected.includes(storeId)
        ? currentSelected.filter(id => id !== storeId)
        : [...currentSelected, storeId];
      
      localStorage.setItem('selectedEtsyStoreIds', JSON.stringify(newSelected));
      return { 
        ...state, 
        selectedStoreIds: newSelected,
        orders: [],
        products: [],
        ordersOffset: 0,
        productsOffset: 0,
        hasMoreOrders: false,
        hasMoreProducts: false,
        lastOrdersFetch: null,
        lastProductsFetch: null
      };
    case 'CLEAR_DATA':
      return { ...initialState, selectedStoreIds: state.selectedStoreIds };
    case 'SET_STATE':
      return { ...state, ...action.payload };
    default:
      return state;
  }
};

interface EtsyContextType extends EtsyState {
  dispatch: React.Dispatch<EtsyAction>;
  hasActiveSubscription: boolean;
  toggleStoreSelection: (storeId: string) => void;
  setSelectedStores: (storeIds: string[]) => void;
}

const EtsyContext = createContext<EtsyContextType | undefined>(undefined);

export const useEtsyContext = () => {
  const context = useContext(EtsyContext);
  if (!context) {
    throw new Error('useEtsyContext must be used within an EtsyProvider');
  }
  return context;
};

export const EtsyProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer(etsyReducer, initialState);
  const { user } = useAuth();
  const { hasActiveSubscription } = useSubscriptionStatus();

  // Load selected stores from localStorage on init
  useEffect(() => {
    const savedStoreIds = localStorage.getItem('selectedEtsyStoreIds');
    if (savedStoreIds) {
      try {
        const storeIds = JSON.parse(savedStoreIds);
        if (Array.isArray(storeIds) && storeIds.length > 0) {
          dispatch({ type: 'SET_SELECTED_STORES', payload: storeIds });
        }
      } catch (error) {
        console.error('Error parsing saved store IDs:', error);
      }
    }
  }, []);

  // Clear data when user logs out or loses subscription
  useEffect(() => {
    if (!user || !hasActiveSubscription) {
      dispatch({ type: 'CLEAR_DATA' });
    }
  }, [user, hasActiveSubscription]);

  const toggleStoreSelection = (storeId: string) => {
    dispatch({ type: 'TOGGLE_STORE_SELECTION', payload: storeId });
  };

  const setSelectedStores = (storeIds: string[]) => {
    dispatch({ type: 'SET_SELECTED_STORES', payload: storeIds });
  };

  const value = {
    ...state,
    dispatch,
    hasActiveSubscription,
    toggleStoreSelection,
    setSelectedStores,
  };

  return <EtsyContext.Provider value={value}>{children}</EtsyContext.Provider>;
};
